import { Controller } from "@hotwired/stimulus"

// Controller for selecting and adding packages to cart
export default class extends Controller {
  static targets = ["package", "quantityInput", "addButton", "message", "packageDetails"]

  static values = {
    addUrl: String,
    opportunityId: Number,
    packages: Array
  }

  connect() {
    console.log("Package Selector Controller connected")
    this.updateButtonState()
  }

  packageChanged() {
    console.log("Package changed:", this.packageTarget.value)
    this.updateButtonState()
    this.updatePackageDetails()
    this.hideMessage()
  }

  quantityChanged() {
    console.log("Quantity changed:", this.quantityInputTarget.value)
    this.updateButtonState()
  }

  updateButtonState() {
    const packageId = this.packageTarget.value
    const quantity = parseInt(this.quantityInputTarget.value) || 0

    // Enable button only if package is selected and quantity > 0
    const shouldEnable = packageId && quantity > 0
    this.addButtonTarget.disabled = !shouldEnable

    console.log("Button state updated:", { packageId, quantity, enabled: shouldEnable })
  }

  updatePackageDetails() {
    if (!this.hasPackageDetailsTarget) return

    const packageId = this.packageTarget.value
    if (!packageId) {
      this.packageDetailsTarget.classList.add('hide')
      this.packageDetailsTarget.innerHTML = ''
      return
    }

    const pkg = this.packagesValue.find(p => p.id.toString() === packageId)
    if (pkg) {
      let html = ''

      if (pkg.description) {
        html += `<p class="text-muted small">${pkg.description}</p>`
      }

      if (pkg.items && pkg.items.length > 0) {
        html += '<p class="small mb-1"><strong>Includes:</strong></p><ul class="small">'
        pkg.items.forEach(item => {
          html += `<li>${item.quantity}x ${item.name}</li>`
        })
        html += '</ul>'
      }

      if (html) {
        this.packageDetailsTarget.innerHTML = html
        this.packageDetailsTarget.classList.remove('hide')
      } else {
        this.packageDetailsTarget.classList.add('hide')
      }
    } else {
      this.packageDetailsTarget.classList.add('hide')
    }
  }

  addToCart() {
    const packageId = this.packageTarget.value
    const quantity = parseInt(this.quantityInputTarget.value) || 1

    if (!packageId) {
      this.showMessage("Please select a package", "warning")
      return
    }

    // Disable button while adding
    this.addButtonTarget.disabled = true
    this.addButtonTarget.innerHTML = '<i class="fas fa-spinner fa-spin me-2"></i>Adding...'

    fetch(this.addUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({
        package_id: packageId,
        quantity: quantity,
        opportunity_id: this.opportunityIdValue
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log("Add package response:", data)
      if (data.success) {
        this.showMessage("Package added to cart!", "success")

        // Reset form
        this.packageTarget.value = ""
        this.quantityInputTarget.value = 1
        if (this.hasPackageDetailsTarget) {
          this.packageDetailsTarget.classList.add('hide')
        }

        // Dispatch event for cart to update
        document.dispatchEvent(new CustomEvent('cartUpdated', { detail: data }))

        // Reload page to show updated cart
        if (data.redirect_url) {
          window.location.href = data.redirect_url
        } else {
          window.location.reload()
        }
      } else {
        this.showMessage(data.error || "Failed to add package", "danger")
      }
    })
    .catch(error => {
      console.error("Error adding package:", error)
      this.showMessage("An error occurred. Please try again.", "danger")
    })
    .finally(() => {
      this.addButtonTarget.disabled = false
      this.addButtonTarget.innerHTML = '<i class="fas fa-plus-circle me-2"></i>Add to Cart'
      this.updateButtonState()
    })
  }

  showMessage(text, type) {
    if (this.hasMessageTarget) {
      this.messageTarget.textContent = text
      this.messageTarget.className = `alert alert-${type} mt-3`
      this.messageTarget.classList.remove('d-none')
    }
  }

  hideMessage() {
    if (this.hasMessageTarget) {
      this.messageTarget.classList.add('d-none')
    }
  }
}
